import classNames from 'classnames';

import { CalendarEventKey, CalendarView, CalendarEventLabel } from 'pages/Calendar/constants';
import { Tooltip } from 'components-antd';
import { Icons } from 'pages/Calendar/Icons';
import { PlusIcon } from 'pages/Radar/RadarKits/components';

import styles from './styles.module.scss';

const labelMap = {
  Milestone: 'Milestones',
  Task: 'Tasks',
  Showing: 'Showings',
  Tour: 'Tours',
  Closing: 'Closings',
  'Project Kickoff': 'Project Kickoffs',
  'Offer Expiration': 'Offers Expiration',
  'Listing Expiration': 'Listings Expiration',
  'Project Deadline': 'Projects Deadline',
};

function formatLabel(count: number, label: string): string {
  return `${count === 1 ? label : labelMap[label]}`;
}

const tileTypes = [
  {
    key: CalendarEventKey.MILESTONES,
    label: CalendarEventLabel.MILESTONES,
    className: styles.milestones,
    iconVariant: Icons.MILESTONES,
  },
  {
    key: CalendarEventKey.TASKS,
    label: CalendarEventLabel.TASKS,
    className: styles.tasks,
    iconVariant: Icons.TASKS,
  },
  {
    key: CalendarEventKey.SHOWINGS,
    label: CalendarEventLabel.SHOWINGS,
    className: styles.showings,
    iconVariant: Icons.SHOWINGS,
  },
  {
    key: CalendarEventKey.TOURS,
    label: CalendarEventLabel.TOURS,
    className: styles.tours,
    iconVariant: Icons.TOURS,
  },
  {
    key: CalendarEventKey.CLOSINGS,
    label: CalendarEventLabel.CLOSINGS,
    className: styles.closing,
    iconVariant: Icons.CLOSING,
  },
  {
    key: CalendarEventKey.PROJECT_KICKOFFS,
    label: CalendarEventLabel.PROJECT_KICKOFFS,
    className: styles.projects,
    iconVariant: Icons.PROJECTS,
  },
  {
    key: CalendarEventKey.EXPIRING_OFFERS,
    label: CalendarEventLabel.EXPIRING_OFFERS,
    className: styles.expiring,
    iconVariant: Icons.EXPIRING_OFFERS,
  },
  {
    key: CalendarEventKey.EXPIRING_LISTINGS,
    label: CalendarEventLabel.EXPIRING_LISTINGS,
    className: styles.expiring,
    iconVariant: Icons.EXPIRING_LISTINGS,
  },
  {
    key: CalendarEventKey.EXPIRING_PROJECTS,
    label: CalendarEventLabel.EXPIRING_PROJECTS,
    className: styles.expiring,
    iconVariant: Icons.EXPIRING_PROJECTS,
  },
];

const TileItem = ({ label, count, className, view, iconVariant }) => {
  return (
    <>
      {view === CalendarView.WEEK ? (
        <div
          className={classNames(
            styles.barTile,
            view === CalendarView.WEEK && styles.weekBarTile,
            className,
          )}
        >
          <Icons variant={iconVariant} />
          <span>
            {count} {label}
          </span>
        </div>
      ) : (
        <Tooltip
          color="white"
          title={
            <div className={styles.tooltipTitle}>
              <Icons variant={iconVariant} />
              {count} {label}
            </div>
          }
          placement="top"
          trigger="hover"
          overlayClassName={styles.calendarTooltipContent}
        >
          <div className={classNames(styles.barTile, className)} />
        </Tooltip>
      )}
    </>
  );
};

export const CalendarTiles = ({ calendarData, date, view }) => {
  const maxTilesToShow = 4;
  const dateItem = calendarData.find((item: any) => item.date === date);
  if (!dateItem) return null;

  const activeTiles = tileTypes.filter(({ key }) => dateItem[key]?.count > 0);

  const visibleTiles = activeTiles.slice(
    0,
    view === CalendarView.MONTH ? maxTilesToShow : activeTiles?.length,
  );
  const overflowTiles = activeTiles.slice(maxTilesToShow);

  return (
    <div className={styles.barTilesContent}>
      {visibleTiles.map(({ key, label, iconVariant, className }) => (
        <TileItem
          key={key}
          label={formatLabel(dateItem[key]?.count, label)}
          count={dateItem[key]?.count}
          className={className}
          view={view}
          iconVariant={iconVariant}
        />
      ))}
      {view === CalendarView.MONTH && overflowTiles?.length > 0 && (
        <Tooltip
          color="white"
          placement="top"
          trigger="hover"
          overlayClassName={styles.calendarTooltipContent}
          title={
            <div className={styles.tooltipOverflowContent}>
              {overflowTiles?.map(({ key, label, iconVariant }, idx) => (
                <div key={idx} className={classNames(styles.tooltipTitle, styles.marginBtm)}>
                  <Icons variant={iconVariant} />
                  <span>
                    {dateItem[key]?.count} {formatLabel(dateItem[key]?.count, label)}
                  </span>
                </div>
              ))}
            </div>
          }
        >
          <div className={styles.moreTile}>
            <PlusIcon />
            {overflowTiles.length} more
          </div>
        </Tooltip>
      )}
    </div>
  );
};
